import React from "react";
import { APPLICATION_STATUS_CONFIG } from "./StatusControl/configs/applicationConfig";

interface StatusCount {
  status: string;
  count: number;
}

interface StatusDistributionBarProps {
  statusCounts: StatusCount[];
  total: number;
  onSegmentClick?: (status: string) => void;
  activeFilter?: string | null;
  height?: number;
}

/**
 * Horizontal stacked bar showing application status distribution.
 * - Colored segments proportional to counts
 * - Clickable segments for filtering
 * - Tooltip on hover showing count
 */
const StatusDistributionBar: React.FC<StatusDistributionBarProps> = ({
  statusCounts,
  total,
  onSegmentClick,
  activeFilter = null,
  height = 24,
}) => {
  if (total === 0) {
    return (
      <div
        style={{
          height,
          backgroundColor: "#f3f4f6",
          borderRadius: "6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "11px",
          color: "#9ca3af",
        }}
      >
        No applications
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        borderRadius: "6px",
        overflow: "hidden",
        height,
        backgroundColor: "#f3f4f6",
      }}
    >
      {statusCounts
        .filter((sc) => sc.count > 0)
        .map((sc) => {
          const config = APPLICATION_STATUS_CONFIG[sc.status];
          const widthPercent = (sc.count / total) * 100;
          const isActive = activeFilter === sc.status;
          const isClickable = !!onSegmentClick;
          const segmentTitle = `${config?.label || sc.status}: ${sc.count}`;
          const segmentLabel = widthPercent > 8 ? sc.count : "";
          const baseStyle: React.CSSProperties = {
            width: `${widthPercent}%`,
            minWidth: sc.count > 0 ? "4px" : 0,
            height: "100%",
            backgroundColor: config?.color || "#6b7280",
            opacity: activeFilter && !isActive ? 0.4 : 1,
            transition: "opacity 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "10px",
            fontWeight: 600,
            color: "white",
            overflow: "hidden",
            whiteSpace: "nowrap",
          };

          if (isClickable) {
            return (
              <button
                key={sc.status}
                type="button"
                onClick={() => onSegmentClick?.(sc.status)}
                title={segmentTitle}
                style={{
                  ...baseStyle,
                  cursor: "pointer",
                  border: "none",
                  padding: 0,
                  fontFamily: "inherit",
                }}
              >
                {segmentLabel}
              </button>
            );
          }

          return (
            <div
              key={sc.status}
              title={segmentTitle}
              style={{ ...baseStyle, cursor: "default" }}
            >
              {segmentLabel}
            </div>
          );
        })}
    </div>
  );
};

export default StatusDistributionBar;
